import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Box,
  CircularProgress,
} from '@mui/material';
import { Document } from '../../types/Document';
import { Category } from '../../types/Category';
import { MergeMissingResolve, MergeTypeMismatchResolve } from '../../types/MergeTypes';
import { styled } from '@mui/material/styles';
import { useHelpContent } from '../../utils/HelpUtils';
import api from '../../services/api';

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    minWidth: '400px',
  },
});

const StyledFormControl = styled(FormControl)({
  marginTop: '16px',
  width: '100%',
});

interface MergeDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  onMerge: (mergeFromDocumentId: string, newName: string, resolveMissing: MergeMissingResolve, resolveTypeMismatch: MergeTypeMismatchResolve) => void;
  documents: Document[];
  currentDocument: Document | undefined;
}

const MergeDocumentDialog: React.FC<MergeDocumentDialogProps> = ({
  open,
  onClose,
  onMerge,
  documents,
  currentDocument,
}) => {
  const [mergeFromDocumentId, setMergeFromDocumentId] = useState('');
  const [newName, setNewName] = useState('');
  const [resolveMissing, setResolveMissing] = useState<MergeMissingResolve>(MergeMissingResolve.COPY);
  const [resolveTypeMismatch, setResolveTypeMismatch] = useState<MergeTypeMismatchResolve>(MergeTypeMismatchResolve.KEEP);
  const [nameError, setNameError] = useState('');
  const [createNewDocument, setCreateNewDocument] = useState(false);
  const { getTooltipTextForState } = useHelpContent();
  const [subCategories, setSubCategories] = useState<string[]>([]);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);

  const availableDocuments = documents.filter(doc => doc.id !== currentDocument?.id);

 const loadSubCategories = async (mergeFrom: string): Promise<string[]> => {
   setLoadingSubCategories(true);
   let loadedSubCategories: string[] = [];

   try {
     // 1. Load current document subcategories
     const res = await api.get('/categories/subcategories', {
       params: { categoryId: currentDocument?.categoryId },
     });
     loadedSubCategories = res.data.map((c: Category) => c.name);

     // 2. If merge is required, load merge-from subcategories
     if (mergeFrom !== '') {
       const mergeFromDoc = documents.find(doc => doc.id === mergeFrom);
       if (mergeFromDoc) {
         try {
           const mergeRes = await api.get('/categories/subcategories', {
             params: { categoryId: mergeFromDoc.categoryId },
           });
           loadedSubCategories.push(...mergeRes.data.map((c: Category) => c.name));
         } catch {
           console.error("Failed to load merge-from subcategories");
         }
       }
     }

     setSubCategories(loadedSubCategories);
     return loadedSubCategories;
   } catch {
     setSubCategories([]);
     return [];
   } finally {
     setLoadingSubCategories(false);
   }
 };

  useEffect(() => {
      if (createNewDocument && currentDocument && mergeFromDocumentId) {
        loadSubCategories(resolveMissing === MergeMissingResolve.COPY ? mergeFromDocumentId : '');
      }
      else {
          setSubCategories([]);
      }
    }, [createNewDocument, currentDocument, mergeFromDocumentId]);

  const normalizeName = (s: string) =>
    (s || '')
      .normalize('NFKC')
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const isDuplicateName = (name: string) => {
    const nn = normalizeName(name);
    return documents.some(doc => 
      normalizeName(doc.label) === nn && 
      doc.id !== currentDocument?.id
    );
  };

  const handleNameChange = (value: string) => {
    const trimmedValue = value.trim();
    if (isDuplicateName(trimmedValue)) {
      setNameError('A document with this name already exists');
    } else if (subCategories.map(normalizeName).includes(normalizeName(trimmedValue))) {
      setNameError('Document contains a sub category with this name');
    } else {
      setNameError('');
    }
    setNewName(value);
  };

  const handleResolveMissingChange = async (value: MergeMissingResolve) => {
    setResolveMissing(value);

    const loadedSubCategories = await loadSubCategories(value === MergeMissingResolve.COPY ? mergeFromDocumentId : '');

    if (loadedSubCategories.map(normalizeName).includes(normalizeName(newName))) {
      setNameError("Document contains a sub category with this name");
    } else {
      setNameError("");
    }
  };

  const handleMergeFromChanged = async (value: string) => {
      setMergeFromDocumentId(value);

      const loadedSubCategories = await loadSubCategories(resolveMissing === MergeMissingResolve.COPY ? value : '');

      if (loadedSubCategories.map(normalizeName).includes(normalizeName(newName))) {
        setNameError("Document contains a sub category with this name");
      } else {
        setNameError("");
      }
    };


  const handleCreateNewDocumentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCreateNewDocument(event.target.checked);
    if (!event.target.checked) {
      setNewName('');
      setNameError('');
    }
  };

  const handleMerge = () => {
    if (createNewDocument && nameError) return;
    onMerge(mergeFromDocumentId, newName === currentDocument?.name ? '' : newName, resolveMissing, resolveTypeMismatch);
    onClose();
  };

  const isMergeButtonDisabled = () => {
    if (!mergeFromDocumentId) return true;
    if (createNewDocument) {
      return !newName.trim() || !!nameError;
    }
    return false;
  };

  return (
    <StyledDialog open={open} onClose={onClose}>
      <DialogTitle>Merge Document</DialogTitle>
      <DialogContent>
        <Tooltip title={getTooltipTextForState('merge-document-from', true)} placement="top">
          <StyledFormControl>
            <InputLabel>Merge From</InputLabel>
            <Select
              value={mergeFromDocumentId}
              onChange={(e) => handleMergeFromChanged(e.target.value)}
              label="Merge From"
            >
              {availableDocuments.map((doc) => (
                <MenuItem key={doc.id} value={doc.id}>
                  {doc.label}
                </MenuItem>
              ))}
            </Select>
          </StyledFormControl>
        </Tooltip>

        <Tooltip title={getTooltipTextForState('merge-document-new', true)} placement="top">
          <FormControlLabel
            control={
              <Checkbox
                checked={createNewDocument}
                onChange={handleCreateNewDocumentChange}
              />
            }
            label="Merge into a new document"
            sx={{ mt: 2 }}
          />
        </Tooltip>

        {createNewDocument && (
         loadingSubCategories ? (
             <Box sx={{ my: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
               <CircularProgress size={18} />
               <Typography variant="body2">Loading groups...</Typography>
             </Box>
           ) :
          <Tooltip title={getTooltipTextForState('merge-document-name', true)} placement="top">
            <TextField
              fullWidth
              label="New Document Name"
              value={newName}
              onChange={(e) => handleNameChange(e.target.value)}
              error={!!nameError}
              helperText={nameError}
              margin="normal"
            />
          </Tooltip>
        )}

        <Tooltip title={getTooltipTextForState('merge-document-missing-resolution', true)} placement="top">
          <StyledFormControl>
            <InputLabel>Missing Resolution</InputLabel>
            <Select
              value={resolveMissing}
              onChange={(e) => handleResolveMissingChange(e.target.value as MergeMissingResolve)}
              label="Missing Resolution"
            >
              <MenuItem value={MergeMissingResolve.COPY}>Copy</MenuItem>
              <MenuItem value={MergeMissingResolve.SKIP}>Skip</MenuItem>
              <MenuItem value={MergeMissingResolve.ABORT}>Abort</MenuItem>
            </Select>
          </StyledFormControl>
        </Tooltip>

        <Tooltip title={getTooltipTextForState('merge-document-type-resolution', true)} placement="top">
          <StyledFormControl>
            <InputLabel>Type Mismatch Resolution</InputLabel>
            <Select
              value={resolveTypeMismatch}
              onChange={(e) => setResolveTypeMismatch(e.target.value as MergeTypeMismatchResolve)}
              label="Type Mismatch Resolution"
            >
              <MenuItem value={MergeTypeMismatchResolve.KEEP}>Keep</MenuItem>
              <MenuItem value={MergeTypeMismatchResolve.SKIP}>Skip</MenuItem>
              <MenuItem value={MergeTypeMismatchResolve.ABORT}>Abort</MenuItem>
            </Select>
          </StyledFormControl>
        </Tooltip>
      </DialogContent>
      <DialogActions>
        <Tooltip title={getTooltipTextForState('cancel', true)} placement="top">
          <Button onClick={onClose}>Cancel</Button>
        </Tooltip>
        <Tooltip title={getTooltipTextForState(isMergeButtonDisabled() ? 'merge-document-merge-disabled' : 'merge-document-merge', true)} placement="top">
          <Button 
            onClick={handleMerge} 
            variant="contained" 
            disabled={isMergeButtonDisabled()}
          >
            Merge
          </Button>
        </Tooltip>
      </DialogActions>
    </StyledDialog>
  );
};

export default MergeDocumentDialog; 